import React, { useRef, useState } from 'react';
import Dropzone from 'react-dropzone';

import Clickable from 'ds/Clickable';
import Grid from 'ds/Grid';
import Icon from 'ds/Icon';
import Layout from 'ds/Layout';
import Spinner from 'ds/Spinner';
import Text from 'ds/Text';
import { Size } from 'ds/Text';
import { generateId } from 'helpers';
import { EditableImage } from 'shared';

import ImageUploadThumbnail from './ImageUploadThumbnail';
import { RequestState } from './types';

interface Props {
  images: EditableImage[];
  onChange: (files: EditableImage[]) => void;
  onDeleteClick?: (image: EditableImage) => void;
  disabled?: boolean;
  countPerRow?: number;
  maxImages?: number;
  label?: string;
  labelSize?: Size;
  thumbnailSize?: number;
  requestState?: RequestState;
}

const ThumbnailImageUploader: React.FC<Props> = ({
  onChange,
  onDeleteClick,
  disabled,
  countPerRow = 4,
  maxImages,
  label,
  labelSize = 'body-md',
  thumbnailSize = 104,
  requestState,
  ...props
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const hiddenFileInputRef = useRef<HTMLInputElement>(null);
  const showFileChooser = () => hiddenFileInputRef.current?.click();

  const images = props.images.filter(i => !i._destroy);

  const handleDrop = (files: File[]) => {
    setIsDragging(false);
    const newImages = files.map(file => ({
      file,
      tempId: generateId()
    }));

    onChange(newImages);
  };

  const input = (
    <input
      ref={hiddenFileInputRef}
      type="file"
      style={{ display: 'none' }}
      accept="image/png, image/jpg, image/heic"
      multiple={maxImages !== 1}
      onChange={event => {
        const files = event.target.files;
        event.stopPropagation();

        if (!files || files.length === 0) {
          // Clear the input value to prevent the same event from firing again
          event.target.value = '';
          return;
        }

        const newImages = Array.from(files).map(file => ({
          file,
          tempId: generateId()
        }));

        onChange(newImages);

        // Clear the input value after processing to allow the same file to be selected again
        event.target.value = '';
      }}
    />
  );

  if (!images.length) {
    return (
      <Layout>
        <Dropzone
          onDragEnter={e => {
            if (e.dataTransfer.items.length > 0) {
              setIsDragging(true);
            }
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          accept="image/png, image/jpg, image/heic"
          disabled={disabled}
          style={{}}
        >
          <Clickable
            onClick={e => {
              e.stopPropagation();
              showFileChooser();
            }}
            style={{ display: 'flex', alignItems: 'center' }}
          >
            <Icon name="attachment" size="xs" color="blue-500" />
            <Text size={labelSize} color="blue-500">
              {label || 'Add product images'}
            </Text>
          </Clickable>
        </Dropzone>
        {input}
      </Layout>
    );
  }

  const addImageThumbnail = (
    <Clickable
      onClick={e => {
        e.stopPropagation();
        showFileChooser();
      }}
      style={{ borderRadius: 8 }}
      disabled={disabled}
      fullWidth
      fullHeight
    >
      <Layout
        align="center"
        justify="center"
        color="gray-25"
        width={thumbnailSize}
        height={thumbnailSize}
        borderRadius={8}
      >
        <Icon name="plus" size="md" color="gray-200" />
      </Layout>
    </Clickable>
  );

  const imageThumbnails = images.map(image => {
    return (
      <ImageUploadThumbnail
        key={image.id || image.tempId || image.url}
        dimension={thumbnailSize}
        disabled={disabled || requestState === 'in_progress'}
        image={image}
        onDeleteClick={!!onDeleteClick ? () => onDeleteClick(image) : undefined}
      />
    );
  });

  return (
    <Layout>
      <Dropzone
        onDragEnter={e => {
          if (e.dataTransfer.items.length > 0) {
            setIsDragging(true);
          }
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        accept="image/png, image/jpg, image/heic"
        disabled={disabled}
        style={{}}
      >
        <div onClick={e => e.stopPropagation()}>
          <Layout position="relative">
            <Layout
              borderWidth={2}
              borderStyle="dashed"
              borderColor={isDragging ? 'blue-500' : 'transparent'}
              borderRadius={12}
              padding={8}
              color={isDragging ? 'blue-50' : 'white'}
              transition="all 0.2s ease-in-out"
            >
              {countPerRow ? (
                <Grid itemsPerRow={countPerRow} gap={8}>
                  {imageThumbnails}
                  {maxImages ? images.length < maxImages && addImageThumbnail : addImageThumbnail}
                </Grid>
              ) : (
                <Layout gap={6} flex>
                  {imageThumbnails}
                </Layout>
              )}
            </Layout>
            {requestState === 'in_progress' && (
              <Layout
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                align="center"
                justify="center"
                color="white"
                opacity={0.8}
                borderRadius={12}
              >
                <Spinner color="blue-500" />
              </Layout>
            )}
          </Layout>
        </div>
      </Dropzone>
      {input}
    </Layout>
  );
};

export default ThumbnailImageUploader;
